import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

export class ApiError extends Error {
  constructor(message: string, public status: number, public details?: unknown) { super(message); }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json', ...options.headers } : options.headers,
  });
  if (response.status === 204) return undefined as T;
  const type = response.headers.get('content-type') ?? '';
  const body = type.includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) throw new ApiError(typeof body === 'object' && body?.error ? body.error : 'Request failed', response.status, body);
  return body as T;
}

export function supportsPasskeys(): boolean {
  return window.isSecureContext && typeof window.PublicKeyCredential !== 'undefined';
}

export function passkeyError(error: unknown, setup = false): string {
  const cause = error && typeof error === 'object' && 'cause' in error ? error.cause : error;
  const name = cause && typeof cause === 'object' && 'name' in cause ? cause.name : '';
  if (name === 'NotAllowedError' || name === 'AbortError') return setup
    ? 'Setup was cancelled or no passkey could be saved. Try again in Safari with iCloud Passwords & Keychain enabled. Your PIN still works.'
    : 'No passkey was selected or unlocked. Sign in with your PIN, then set up Face ID / passkey in Settings.';
  if (name === 'InvalidStateError') return 'This device already has a passkey for your account. Try Use Face ID / passkey at sign-in.';
  if (name === 'SecurityError' || name === 'NotSupportedError') return 'Open Harden Up directly in Safari over HTTPS to use Face ID / passkey. You can still sign in with your PIN.';
  if (name === 'ConstraintError') return 'This device could not save a passkey with a screen lock. Enable your device lock and try again in Safari. Your PIN still works.';
  if (error instanceof ApiError && error.status === 401) return 'This passkey is no longer linked to an active account. Sign in with your PIN and set up Face ID / passkey in Settings.';
  return error instanceof Error ? error.message : 'Passkey could not be used. Your phone number and PIN still work.';
}

// Keep challenge fetching on the click's native fetch chain for older Safari.
async function passkeyOptions<T>(path: string, body = '{}'): Promise<T> {
  if (!supportsPasskeys()) throw new Error('Use Safari on a device that supports passkeys, or sign in with your PIN.');
  const response = await fetch(`/api${path}`, {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body});
  const request = await response.json();
  if (!response.ok) throw new ApiError(request.error || 'Passkey could not be prepared.', response.status);
  return request as T;
}

export async function registerPasskey(deviceLabel = 'iPhone'): Promise<void> {
  const request = await passkeyOptions<{ options: Parameters<typeof startRegistration>[0]['optionsJSON']; challengeId: string }>('/auth/passkey/register/options');
  const response = await startRegistration({ optionsJSON: request.options });
  await api('/auth/passkey/register/verify', { method: 'POST', body: JSON.stringify({ challengeId: request.challengeId, response, deviceLabel }) });
}

export async function signInWithPasskey(phone?: string): Promise<void> {
  const request = await passkeyOptions<{ options: Parameters<typeof startAuthentication>[0]['optionsJSON']; challengeId: string }>('/auth/passkey/login/options', JSON.stringify({ phone }));
  const response = await startAuthentication({ optionsJSON: request.options });
  await api('/auth/passkey/login/verify', { method: 'POST', body: JSON.stringify({ challengeId: request.challengeId, response }) });
}
